<template>
  <teleport to="#app">
    <div
      v-if="isReady && displayPersonalitySelector"
      class="lf-btn-action lf-btn-action--active"
      @click.stop="handleShowPersonalities"
    >
      <span class="lf-btn-action__icon icon-game-personality-test"></span>
      <div class="lf-btn-action__label">Select your personality</div>
      <ul v-if="isOpen">
        <li
          v-for="(personality, index) in personalities"
          :key="index"
          :value="personality.id"
          :class="{ active: personality.id === selectedPersonality?.id }"
          @click.stop="handleSelectPersonality(personality)"
        >
          {{ personality.name }}
        </li>
      </ul>
    </div>
  </teleport>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeMount, ref, watch } from 'vue';
import type { AddonGameplayModel } from '@/src/components/addons/gameplay/Model';
import type {
  GamePersonalityTestPersonalityState,
  GamePersonalityTestState
} from '@/src/components/games/personality-test/Model';
import { GamePersonalityTestModel } from '@/src/components/games/personality-test/Model';
import type { ReplacementTags } from '@/src/store/campaign';
import { useCampaignStore } from '@/src/store/campaign';

export default defineComponent({
  name: 'PersonalityTestGameInitializer',
  setup() {
    const campaignStore = useCampaignStore();
    const personalities = ref<GamePersonalityTestPersonalityState[]>([]);
    const isReady = ref(false);
    const isOpen = ref(false);
    const selectedPersonality = ref<GamePersonalityTestPersonalityState>();

    const displayPersonalitySelector = computed(() => {
      return campaignStore.model?.state.edit?.enabled ?? false;
    });

    const fetchPersonlitiesFromState = () => {
      let personalityTestGameState: GamePersonalityTestState | undefined;

      campaignStore.model?.state.flowPages.forEach((pageModel) => {
        pageModel.getAddons<AddonGameplayModel>('gameplay').forEach((gameplayModel) => {
          const personalityTestGameModel = gameplayModel.state.settings?.game;

          if (personalityTestGameModel instanceof GamePersonalityTestModel) {
            personalityTestGameState = personalityTestGameModel?.state;
            personalities.value = personalityTestGameState?.personalities;
            isReady.value = true;
          }
        });
      });
    };

    const replacementTags = computed<ReplacementTags>(() => {
      let replacementTagData: ReplacementTags = {};
      if (selectedPersonality.value) {
        replacementTagData = {
          personality_image: selectedPersonality.value.image
            ? `<img src="${selectedPersonality.value.image}" class="image image--personality" alt="" />`
            : '',
          personality_image_origin: selectedPersonality.value.image,
          personality_image_url: selectedPersonality.value.image,
          personality_name: selectedPersonality.value.name,
          personality_description: selectedPersonality.value.description,
          personality_url: selectedPersonality.value.url,
          personality_video_url: selectedPersonality.value.videoUrl
        };
      }
      return replacementTagData;
    });

    const handleShowPersonalities = () => {
      isOpen.value = !isOpen.value;
    };

    const handleSelectPersonality = (personality: GamePersonalityTestPersonalityState) => {
      isOpen.value = false;
      selectedPersonality.value = personality;
    };

    onBeforeMount(() => {
      if (displayPersonalitySelector.value) {
        fetchPersonlitiesFromState();
      }
    });

    watch(selectedPersonality, () => {
      if (!selectedPersonality.value) {
        return;
      }

      campaignStore.addReplacementTags(replacementTags.value);

      if (campaignStore.metricData) {
        campaignStore.metricData.personality = selectedPersonality.value.id;
      } else {
        campaignStore.metricData = { personality: selectedPersonality.value.id };
      }
    });

    return {
      displayPersonalitySelector,
      isOpen,
      isReady,
      personalities,
      selectedPersonality,
      handleSelectPersonality,
      handleShowPersonalities
    };
  }
});
</script>

<style lang="scss" scoped>
.lf-btn-action {
  position: fixed;
  right: 90px;
  bottom: 30px;
  display: block;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #131313;
  line-height: 43px;
  font-size: 16px;
  color: #ffffff;
  text-align: center;
  cursor: pointer;

  ul {
    position: absolute;
    transform: translateX(-105%) translateY(-100%);
    background-color: #333;
    color: #fff;
    list-style: none;
    padding: 0;
    margin: 0;
    width: 160px;
    max-height: 200px;
    overflow: auto;
  }
  li {
    line-height: 1.8rem;
    list-style: none;
    background-color: #3e3e3e !important;
    color: #959595 !important;
    font-size: 12px !important;
    font-family: Gilroy, Helvetica, Arial, sans-serif;
    padding: 6px 10px !important;
    text-align: left;
    transition: all 0.3s ease;

    &.active {
      background-color: rgba(255, 255, 255, 0.15) !important;
      color: #fff !important;
    }

    &:hover {
      background-color: rgba(255, 255, 255, 0.15) !important;
      color: #fff !important;
    }
  }
}

.lf-btn-action__icon {
  opacity: 0.5;
  transition: opacity 175ms linear;
}

.lf-btn-action--active .lf-btn-action__icon {
  opacity: 1;
}

.lf-btn-action__label {
  display: block;
  position: absolute;
  top: 50%;
  right: calc(100% + 10px);
  transform: translateY(-50%);
  padding: 4px 5px;
  background: #202020;
  border-radius: 4px;
  font-family: 'Gilroy', Helvetica, Arial, sans-serif;
  font-size: 11px;
  line-height: 11px;
  letter-spacing: 0;
  white-space: nowrap;
  color: #d8d8d8;
  opacity: 0;
  transition: opacity 150ms linear;
  pointer-events: none;
}

.lf-btn-action:hover .lf-btn-action__label {
  opacity: 1;
}

.lf-personality-dropdown {
  box-shadow: none !important;
  padding: 0 !important;
  background-color: #3e3e3e !important;
  border: none;
  border-radius: 0 !important;

  select {
    display: none;
  }

  .select2 {
    position: absolute;
    right: 100%;
    bottom: 0;
    margin-right: 5px;
    width: 160px !important;
  }

  .select2-selection {
    display: none;
  }

  .select2-results__option:first-child:empty {
    display: none;
  }

  .select2-results {
    box-shadow: 0 4px 7px 0 rgba(0, 0, 0, 0.2);
  }
}
</style>
